import type { ReactElement, ReactNode, SVGProps } from 'react'

/*
 * Ikony k1p4 — jednokreskowe SVG 24×24: stroke currentColor, stroke-width 2,
 * zaokrąglone końce i złączenia, fill none. Bez emoji (CLAUDE.md).
 */

export interface IconProps extends SVGProps<SVGSVGElement> {
  /** Bok ikony (domyślnie 24). */
  size?: number
}

type IconComponent = (props: IconProps) => ReactElement

function createIcon(displayName: string, children: ReactNode): IconComponent {
  function Icon({ size = 24, ...rest }: IconProps): ReactElement {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...rest}
      >
        {children}
      </svg>
    )
  }
  Icon.displayName = displayName
  return Icon
}

export const ChevronUp = createIcon('ChevronUp', <path d="M6 15l6-6 6 6" />)

export const ChevronDown = createIcon('ChevronDown', <path d="M6 9l6 6 6-6" />)

export const ChevronLeft = createIcon('ChevronLeft', <path d="M15 6l-6 6 6 6" />)

export const ChevronRight = createIcon('ChevronRight', <path d="M9 6l6 6-6 6" />)

export const Menu = createIcon(
  'Menu',
  <>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </>,
)

export const Bell = createIcon(
  'Bell',
  <>
    <path d="M18 16v-5.5a6 6 0 0 0-12 0V16l-1.5 2h15z" />
    <path d="M10 21a2.2 2.2 0 0 0 4 0" />
  </>,
)

export const Search = createIcon(
  'Search',
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M15.8 15.8 21 21" />
  </>,
)

export const Globe = createIcon(
  'Globe',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a13.5 13.5 0 0 1 0 18 13.5 13.5 0 0 1 0-18" />
  </>,
)

export const User = createIcon(
  'User',
  <>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
  </>,
)

export const Users = createIcon(
  'Users',
  <>
    <circle cx="9" cy="8.5" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 5.5a3.5 3.5 0 0 1 0 6.5" />
    <path d="M17.5 14.5a6.5 6.5 0 0 1 4 5.5" />
  </>,
)

export const Plus = createIcon(
  'Plus',
  <>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </>,
)

export const X = createIcon(
  'X',
  <>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </>,
)

export const Check = createIcon('Check', <path d="M5 12.5l4.5 4.5L19 7.5" />)

export const Warning = createIcon(
  'Warning',
  <>
    <path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </>,
)

export const Info = createIcon(
  'Info',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </>,
)

export const Document = createIcon(
  'Document',
  <>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h6" />
  </>,
)

export const Calendar = createIcon(
  'Calendar',
  <>
    <rect x="4" y="5" width="16" height="16" rx="2" />
    <path d="M8 3v4" />
    <path d="M16 3v4" />
    <path d="M4 11h16" />
  </>,
)

export const Clock = createIcon(
  'Clock',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </>,
)

export const Settings = createIcon(
  'Settings',
  <>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2" />
    <circle cx="12" cy="12" r="3" />
  </>,
)

export const LogOut = createIcon(
  'LogOut',
  <>
    <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </>,
)

export const Home = createIcon(
  'Home',
  <>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.8V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.8" />
    <path d="M10 21v-6h4v6" />
  </>,
)

export const Briefcase = createIcon(
  'Briefcase',
  <>
    <rect x="3" y="7.5" width="18" height="12.5" rx="2" />
    <path d="M9 7.5v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    <path d="M3 13h18" />
  </>,
)

export const Heart = createIcon(
  'Heart',
  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.51 4.04 3 5.5l7 7z" />,
)

export const ChartBar = createIcon(
  'ChartBar',
  <>
    <path d="M4 20h16" />
    <path d="M7 20v-7" />
    <path d="M12 20V5" />
    <path d="M17 20V10" />
  </>,
)

export const Link = createIcon(
  'Link',
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>,
)

export const Workflow = createIcon(
  'Workflow',
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
    <path d="M10 6.5h5a2 2 0 0 1 2 2V14" />
  </>,
)

export const GraduationCap = createIcon(
  'GraduationCap',
  <>
    <path d="M22 10 12 5 2 10l10 5z" />
    <path d="M6 12.5V17c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.5" />
    <path d="M22 10v5" />
  </>,
)

export const Shield = createIcon(
  'Shield',
  <path d="M12 3l7 2.5V11c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V5.5z" />,
)

export const Inbox = createIcon(
  'Inbox',
  <>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11" />
  </>,
)

export const Grid = createIcon(
  'Grid',
  <>
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
  </>,
)

export const Wallet = createIcon(
  'Wallet',
  <>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a1 1 0 0 1 1 1v2" />
    <rect x="3" y="7.5" width="18" height="12" rx="2" />
    <path d="M16 13.5h2.5" />
  </>,
)

export const Bank = createIcon(
  'Bank',
  <>
    <path d="M3 9.5 12 4l9 5.5" />
    <path d="M5 9.5V18" />
    <path d="M10 9.5V18" />
    <path d="M14 9.5V18" />
    <path d="M19 9.5V18" />
    <path d="M3 21h18" />
  </>,
)

export const Plug = createIcon(
  'Plug',
  <>
    <path d="M9 3v5" />
    <path d="M15 3v5" />
    <path d="M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6z" />
    <path d="M12 17v4" />
  </>,
)

export const ShieldPlus = createIcon(
  'ShieldPlus',
  <>
    <path d="M12 3l7 2.5V11c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V5.5z" />
    <path d="M12 8.5v5" />
    <path d="M9.5 11h5" />
  </>,
)

/* Pomoc — link do podręcznika użytkownika /docs (ADR-0062). */
export const HelpCircle = createIcon(
  'HelpCircle',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7" />
    <path d="M12 17h.01" />
  </>,
)

export const Lightbulb = createIcon(
  'Lightbulb',
  <>
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .8 1.6V18h5.4v-2.5c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3z" />
  </>,
)
